import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, AlertTriangle, CheckCircle2, Clock, CalendarDays,
  Users, DollarSign, PieChart as PieChartIcon, TrendingUp,
  Download, FileText, FileSpreadsheet,
} from 'lucide-react';
import { Sidebar, Topbar, CategoryChip, TASK_CATEGORY_ORDER } from './Dashboard';
import { auth, tasks, clients, invoices as invoicesApi, team, getStoredUser } from '../api';
import { FullscreenLoading } from './Time';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { downloadCSV, openPrintablePDF } from '../utils/exports';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CATEGORY_HEX = {
  'VAT': '#10b981', 'PAYE': '#3b82f6', 'CIPC': '#a855f7',
  'Beneficial Ownership': '#d946ef', 'Taxation': '#f59e0b',
  'Provisional Tax': '#f97316', 'Bookkeeping': '#14b8a6',
  'Audit': '#ef4444', 'Advisory': '#6366f1', 'Other': '#a8a29e',
};

const PERIODS = [
  { value: 'all', label: 'All time' },
  { value: 'month', label: 'This month' },
  { value: 'quarter', label: 'This quarter' },
  { value: 'year', label: 'This year' },
];

function periodStart(period) {
  const now = new Date();
  if (period === 'month') return new Date(now.getFullYear(), now.getMonth(), 1);
  if (period === 'quarter') {
    const q = Math.floor(now.getMonth() / 3) * 3;
    return new Date(now.getFullYear(), q, 1);
  }
  if (period === 'year') return new Date(now.getFullYear(), 0, 1);
  return null;
}

const money = (n) => 'R ' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function Reports() {
  const nav = useNavigate();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [taskList, setTaskList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [invoiceList, setInvoiceList] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [period, setPeriod] = useState('all');
  const [invoiceStatus, setInvoiceStatus] = useState('all'); // all | draft | sent | partial | paid

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    (async () => {
      try {
        const [me, tl, cl, iv, tm] = await Promise.all([
          auth.me().catch(() => null),
          tasks.list(),
          clients.list(),
          invoicesApi.list().catch(() => []),
          team.list().catch(() => ({ members: [] })),
        ]);
        if (me) setUser(me);
        setTaskList(tl);
        setClientList(cl);
        setInvoiceList(Array.isArray(iv) ? iv : []);
        setTeamMembers(tm.members || []);
      } finally { setLoading(false); }
    })();
  }, [nav]);

  // Filter by period based on task.createdAt / invoice.createdAt
  const startCutoff = periodStart(period);

  const scopedTasks = useMemo(() => {
    if (!startCutoff) return taskList;
    return taskList.filter((t) => {
      const ref = t.createdAt ? new Date(t.createdAt) : null;
      return ref && ref >= startCutoff;
    });
  }, [taskList, startCutoff]);

  const scopedInvoices = useMemo(() => {
    let list = invoiceList;
    if (startCutoff) list = list.filter((i) => i.createdAt && new Date(i.createdAt) >= startCutoff);
    if (invoiceStatus !== 'all') list = list.filter((i) => (i.status || 'draft') === invoiceStatus);
    return list;
  }, [invoiceList, startCutoff, invoiceStatus]);

  const stats = useMemo(() => {
    const now = new Date();
    const total = scopedTasks.length;
    const done = scopedTasks.filter((t) => t.status === 'Done').length;
    const open = total - done;
    const overdue = scopedTasks.filter((t) => t.status !== 'Done' && t.dueDate && new Date(t.dueDate) < now).length;
    const hours = scopedTasks.reduce((a, t) => a + (t.hours || 0), 0);

    // Category breakdown
    const catCounts = {};
    const catHours = {};
    scopedTasks.forEach((t) => {
      const c = t.category || 'Other';
      catCounts[c] = (catCounts[c] || 0) + 1;
      catHours[c] = (catHours[c] || 0) + (t.hours || 0);
    });

    // Hours by team member (only members with hours>0 or a task assigned)
    const memberHours = {}; // { name: { hours, tasksDone, tasksOpen } }
    scopedTasks.forEach((t) => {
      const name = t.assignee || 'Unassigned';
      const rec = memberHours[name] || { hours: 0, tasksDone: 0, tasksOpen: 0 };
      rec.hours += t.hours || 0;
      if (t.status === 'Done') rec.tasksDone += 1; else rec.tasksOpen += 1;
      memberHours[name] = rec;
    });
    const memberRows = Object.entries(memberHours)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.hours - a.hours);

    // Revenue by client
    const clientRev = {}; // { clientName: { total, invoices, hours } }
    scopedInvoices.forEach((inv) => {
      const name = inv.clientName || 'Unknown client';
      const rec = clientRev[name] || { total: 0, invoices: 0, hours: 0 };
      rec.total += Number(inv.total || 0);
      rec.hours += Number(inv.totalHours || 0);
      rec.invoices += 1;
      clientRev[name] = rec;
    });
    const revRows = Object.entries(clientRev)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.total - a.total);

    const revenueTotal = revRows.reduce((a, r) => a + r.total, 0);
    const invoicesPaid = scopedInvoices.filter((i) => i.status === 'paid').length;
    const outstandingTotal = scopedInvoices
      .filter((i) => ['sent', 'partial'].includes(i.status))
      .reduce((a, i) => a + Number(i.balance ?? i.total ?? 0), 0);

    // Deadline heatmap: tasks per month for next 6 months (uses all tasks, not period-scoped)
    const heat = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const label = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
      const count = taskList.filter((t) => {
        if (!t.dueDate || t.status === 'Done') return false;
        const td = new Date(t.dueDate);
        return td.getFullYear() === d.getFullYear() && td.getMonth() === d.getMonth();
      }).length;
      heat.push({ label, count });
    }

    return {
      total, done, open, overdue, hours,
      catCounts, catHours,
      memberRows, revRows, revenueTotal, invoicesPaid, outstandingTotal,
      heat,
    };
  }, [scopedTasks, scopedInvoices, taskList]);

  if (loading) return <FullscreenLoading label="Loading reports..."/>;

  const memberMaxHours = Math.max(1, ...stats.memberRows.map((r) => r.hours));
  const revMax = Math.max(1, ...stats.revRows.map((r) => r.total));
  const heatMax = Math.max(1, ...stats.heat.map((h) => h.count));
  const catTotal = Object.values(stats.catCounts).reduce((a, b) => a + b, 0) || 1;

  const periodLabel = PERIODS.find((p) => p.value === period)?.label || 'All time';
  const fileSuffix = `${periodLabel.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}`;

  // Prebuilt section payloads for export
  const membersHeaders = ['Team member', 'Hours', 'Tasks done', 'Tasks open'];
  const membersRows = stats.memberRows.map((m) => [m.name, m.hours.toFixed(2), m.tasksDone, m.tasksOpen]);

  const categoryHeaders = ['Category', 'Tasks', '% of total', 'Hours'];
  const categoryRows = TASK_CATEGORY_ORDER
    .filter((c) => c !== 'All' && stats.catCounts[c])
    .map((c) => [c, stats.catCounts[c], `${((stats.catCounts[c] / catTotal) * 100).toFixed(0)}%`, (stats.catHours[c] || 0).toFixed(2)]);

  const revenueHeaders = ['Client', 'Invoices', 'Hours', 'Total (ZAR)'];
  const revenueRows = stats.revRows.map((r) => [r.name, r.invoices, r.hours.toFixed(2), Math.round(r.total).toLocaleString()]);

  const kpiHeaders = ['Metric', 'Value'];
  const kpiRows = [
    ['Revenue', money(stats.revenueTotal)],
    ['Outstanding', money(stats.outstandingTotal)],
    ['Invoices paid', stats.invoicesPaid],
    ['Hours logged', stats.hours.toFixed(2) + 'h'],
    ['Tasks in period', stats.total],
    ['Completed', stats.done],
    ['Open', stats.open],
    ['Overdue', stats.overdue],
    ['Clients tracked', clientList.length],
  ];

  const exportSection = (kind, sectionKey) => {
    const map = {
      members: { title: 'Hours by team member', headers: membersHeaders, rows: membersRows },
      category: { title: 'Category mix', headers: categoryHeaders, rows: categoryRows },
      revenue: { title: 'Revenue by client', headers: revenueHeaders, rows: revenueRows },
    };
    const s = map[sectionKey];
    if (!s) return;
    if (kind === 'csv') {
      downloadCSV(`accu-desk-${sectionKey}-${fileSuffix}`, s.headers, s.rows);
    } else {
      openPrintablePDF({
        title: s.title,
        subtitle: `${periodLabel} · Accu Desk practice report`,
        firmName: user?.firm,
        sections: [s],
      });
    }
  };

  const exportFullReport = (kind) => {
    const combined = [
      { title: 'Summary KPIs', headers: kpiHeaders, rows: kpiRows },
      { title: 'Hours by team member', headers: membersHeaders, rows: membersRows },
      { title: 'Category mix', headers: categoryHeaders, rows: categoryRows },
      { title: 'Revenue by client', headers: revenueHeaders, rows: revenueRows },
    ];
    if (kind === 'csv') {
      // For CSV of full report, concatenate as separate labeled blocks
      const lines = [];
      combined.forEach((s, i) => {
        if (i > 0) lines.push(['']);
        lines.push([s.title.toUpperCase()]);
        lines.push(s.headers);
        s.rows.forEach((r) => lines.push(r));
      });
      // Flatten via downloadCSV using stitched headers/rows — reuse helper with a marker header
      downloadCSV(`accu-desk-full-report-${fileSuffix}`, lines[0], lines.slice(1));
    } else {
      openPrintablePDF({
        title: 'Accu Desk practice report',
        subtitle: `${periodLabel} · Generated for the board`,
        firmName: user?.firm,
        sections: combined,
      });
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Reports"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2">
                <BarChart3 size={24} className="text-emerald-800"/> Reports
              </h1>
              <p className="mt-1 text-stone-600 text-sm">Live practice metrics — team performance, client revenue and workload mix.</p>
            </div>
            <div className="flex items-end gap-2" data-testid="reports-period-picker">
              <div>
                <div className="text-xs text-stone-500 mb-1">Period</div>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="h-9 w-40" data-testid="period-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="h-9 bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="export-full-report-btn">
                    <Download size={14} className="mr-2"/> Export full report
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="text-[11px] text-stone-500 uppercase tracking-wider">Full report ({periodLabel})</DropdownMenuLabel>
                  <DropdownMenuSeparator/>
                  <DropdownMenuItem onClick={() => exportFullReport('csv')} data-testid="export-full-csv">
                    <FileSpreadsheet size={14} className="mr-2 text-emerald-700"/> CSV (Excel)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportFullReport('pdf')} data-testid="export-full-pdf">
                    <FileText size={14} className="mr-2 text-sky-700"/> PDF (board-ready)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="reports-kpis">
            <StatCard icon={DollarSign} label="Revenue" value={money(stats.revenueTotal)} sub={`${stats.invoicesPaid} paid · ${money(stats.outstandingTotal)} outstanding`} tone="good" testid="kpi-revenue"/>
            <StatCard icon={Clock} label="Hours logged" value={stats.hours.toFixed(1) + 'h'} sub={`${stats.total} tasks in period`} testid="kpi-hours"/>
            <StatCard icon={CheckCircle2} label="Completed" value={stats.done} sub={`${stats.open} still open`} tone="good" testid="kpi-done"/>
            <StatCard icon={AlertTriangle} label="Overdue" value={stats.overdue} tone={stats.overdue > 0 ? 'warn' : 'default'} sub={`${clientList.length} clients tracked`} testid="kpi-overdue"/>
          </div>

          {/* Row 1: Hours by team member + Category donut */}
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="hours-by-member">
              <div className="p-5 border-b border-stone-100 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-stone-900 flex items-center gap-2"><Users size={16} className="text-emerald-800"/> Hours by team member</div>
                  <div className="text-xs text-stone-500 mt-0.5">Utilisation across your firm.</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-xs text-stone-500">
                    Team of <span className="font-semibold text-stone-800">{teamMembers.length || 1}</span>
                  </div>
                  <ExportMenu onExport={(k) => exportSection(k, 'members')} testid="export-members"/>
                </div>
              </div>
              <div className="p-5 space-y-4">
                {stats.memberRows.length === 0 && <div className="text-sm text-stone-500">No time logged in this period.</div>}
                {stats.memberRows.map((m) => {
                  const initials = m.name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
                  const pct = (m.hours / memberMaxHours) * 100;
                  return (
                    <div key={m.name} data-testid={`member-row-${m.name}`}>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-emerald-100 text-emerald-900 text-[11px] font-semibold">{initials || 'U'}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium text-stone-900 truncate">{m.name}</span>
                            <span className="tabular-nums text-stone-800 font-semibold">{m.hours.toFixed(2)}h</span>
                          </div>
                          <div className="text-[11px] text-stone-500 mt-0.5">
                            {m.tasksDone} done · {m.tasksOpen} open
                          </div>
                          <div className="mt-1.5 h-2 rounded-full bg-stone-100 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-800" style={{ width: `${pct}%` }}/>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="category-mix">
              <div className="p-5 border-b border-stone-100 flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-stone-900 flex items-center gap-2"><PieChartIcon size={16} className="text-emerald-800"/> Category mix</div>
                  <div className="text-xs text-stone-500 mt-0.5">Share of tasks by compliance stream.</div>
                </div>
                <ExportMenu onExport={(k) => exportSection(k, 'category')} testid="export-category"/>
              </div>
              <div className="p-5 flex flex-col items-center gap-4">
                <Donut data={TASK_CATEGORY_ORDER.filter((c) => c !== 'All' && stats.catCounts[c]).map((c) => ({
                  label: c, value: stats.catCounts[c], color: CATEGORY_HEX[c] || '#a8a29e',
                }))}/>
                <div className="w-full space-y-1.5">
                  {TASK_CATEGORY_ORDER.filter((c) => c !== 'All' && stats.catCounts[c]).map((c) => {
                    const v = stats.catCounts[c];
                    const pct = ((v / catTotal) * 100).toFixed(0);
                    return (
                      <div key={c} className="flex items-center justify-between text-xs" data-testid={`cat-${c}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ background: CATEGORY_HEX[c] || '#a8a29e' }}/>
                          <span className="text-stone-700 truncate">{c}</span>
                        </div>
                        <span className="tabular-nums text-stone-600">
                          <span className="font-semibold text-stone-800">{v}</span> · {pct}%
                        </span>
                      </div>
                    );
                  })}
                  {Object.keys(stats.catCounts).length === 0 && <div className="text-sm text-stone-500 text-center py-4">No tasks yet.</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Revenue by client */}
          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="revenue-by-client">
            <div className="p-5 border-b border-stone-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="font-semibold text-stone-900 flex items-center gap-2"><DollarSign size={16} className="text-emerald-800"/> Revenue by client</div>
                <div className="text-xs text-stone-500 mt-0.5">Invoiced value across clients in the selected period.</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-500">Status</span>
                <Select value={invoiceStatus} onValueChange={setInvoiceStatus}>
                  <SelectTrigger className="h-9 w-32" data-testid="invoice-status-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All invoices</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="partial">Part paid</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
                <ExportMenu onExport={(k) => exportSection(k, 'revenue')} testid="export-revenue"/>
              </div>
            </div>
            <div className="p-5">
              {stats.revRows.length === 0 ? (
                <div className="text-sm text-stone-500 text-center py-8">
                  <TrendingUp size={22} className="mx-auto text-stone-300 mb-2"/>
                  No invoiced revenue yet in this period. Create an invoice from the Billing page.
                </div>
              ) : (
                <div className="space-y-3">
                  {stats.revRows.slice(0, 10).map((r, i) => {
                    const pct = (r.total / revMax) * 100;
                    return (
                      <div key={r.name} data-testid={`rev-row-${r.name}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="h-5 w-5 rounded-full bg-emerald-100 text-emerald-900 grid place-items-center text-[10px] font-semibold">{i + 1}</span>
                            <span className="font-medium text-stone-900 truncate">{r.name}</span>
                            <span className="text-[11px] text-stone-500">· {r.invoices} invoice{r.invoices === 1 ? '' : 's'} · {r.hours.toFixed(1)}h</span>
                          </div>
                          <span className="tabular-nums font-semibold text-stone-900">{money(r.total)}</span>
                        </div>
                        <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-amber-400 to-emerald-600" style={{ width: `${pct}%` }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Row 3: Deadline heatmap */}
          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
            <div className="p-5 border-b border-stone-100">
              <div className="font-semibold text-stone-900 flex items-center gap-2"><CalendarDays size={16} className="text-emerald-800"/> Upcoming deadlines by month</div>
              <div className="text-xs text-stone-500 mt-0.5">The next 6 months of open work.</div>
            </div>
            <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {stats.heat.map((h) => {
                const pct = (h.count / heatMax);
                const bg = h.count === 0 ? 'bg-stone-50' : pct > 0.66 ? 'bg-red-50' : pct > 0.33 ? 'bg-amber-50' : 'bg-emerald-50';
                return (
                  <div key={h.label} className={`rounded-lg border border-stone-200 ${bg} p-4`}>
                    <div className="text-xs text-stone-500">{h.label}</div>
                    <div className="mt-1 text-2xl font-serif font-semibold text-stone-900 tabular-nums">{h.count}</div>
                    <div className="text-xs text-stone-500 mt-0.5">task{h.count === 1 ? '' : 's'}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, tone, testid }) {
  const toneCls = tone === 'good' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : 'text-stone-900';
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-5 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-center gap-2 text-stone-500 text-xs font-medium"><Icon size={14}/> {label}</div>
      <div className={`mt-2 text-3xl font-serif font-semibold tabular-nums ${toneCls}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-stone-500">{sub}</div>}
    </div>
  );
}

function ExportMenu({ onExport, size = 'sm', label = 'Export', testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size={size} className="h-8 border-stone-300 text-stone-700 hover:text-sky-700 hover:border-sky-400" data-testid={testid}>
          <Download size={13} className="mr-1.5"/> {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel className="text-[11px] text-stone-500 uppercase tracking-wider">Download as</DropdownMenuLabel>
        <DropdownMenuSeparator/>
        <DropdownMenuItem onClick={() => onExport('csv')} data-testid={testid ? `${testid}-csv` : undefined}>
          <FileSpreadsheet size={14} className="mr-2 text-emerald-700"/> CSV (Excel)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onExport('pdf')} data-testid={testid ? `${testid}-pdf` : undefined}>
          <FileText size={14} className="mr-2 text-sky-700"/> PDF (print)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Simple SVG donut. `data` = [{ label, value, color }]. Renders empty-state ring if all zero.
 */
function Donut({ data, size = 160, thickness = 22 }) {
  const total = data.reduce((a, d) => a + d.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f5f5f4" strokeWidth={thickness}/>
      {total > 0 && data.map((d, i) => {
        const frac = d.value / total;
        const dash = c * frac;
        const gap = c - dash;
        const seg = (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            fill="none" stroke={d.color} strokeWidth={thickness}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
        offset += dash;
        return seg;
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" className="fill-stone-900" style={{ fontFamily: 'ui-serif, Georgia, serif', fontSize: 22, fontWeight: 600 }}>
        {total}
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" className="fill-stone-500" style={{ fontSize: 10 }}>
        {total === 1 ? 'task' : 'tasks'}
      </text>
    </svg>
  );
}
